import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { PageGallery } from "@/components/PageGallery";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import type { NaatiDocLanding } from "@/content/naati-corpus/documents";
import { NAATI_DOC_META, NAATI_PROVINCE_META } from "@/content/naati-corpus/meta.gen";

function docFaqs(d: NaatiDocLanding) {
  return [
    { q: `${d.titleTh} ใช้ยื่นที่ไหนได้บ้าง?`, a: d.useCaseTh },
    { q: `ต้องเตรียมอะไรก่อนส่ง${d.officialTh}มาแปล?`, a: d.prepareTh.join(" · ") },
    { q: `ข้อผิดพลาดที่พบบ่อยของ${d.titleTh}คืออะไร?`, a: d.pitfallsTh.join(" · ") },
    { q: `${d.titleTh} ใช้เวลาและราคาเท่าไหร่?`, a: `${d.turnaroundTh} · ราคาคิดตามจำนวนหน้า/จำนวนคำ ขอใบเสนอราคาฟรีก่อนเริ่มงานได้ทุกครั้ง` },
    {
      q: `คำแปลใช้ยื่น ImmiAccount ได้เลยหรือไม่?`,
      a: "ได้ครับ ไฟล์ PDF ที่ลงตราประทับและหมายเลข CPN ของนักแปล NAATI Certified อัปโหลดเข้า ImmiAccount ได้โดยตรง และใช้กับ Immigration NZ, IRCC แคนาดา และ UK Home Office ได้ด้วย",
    },
    {
      q: `ต้องส่งต้นฉบับตัวจริงไหม?`,
      a: "สำหรับการแปล NAATI ใช้สแกนสีความละเอียดสูงได้ ต้นฉบับจำเป็นเฉพาะกรณีทำนิติกรณ์กรมการกงสุล (MFA) ควบคู่ไปด้วย",
    },
  ];
}

export const Route = createFileRoute("/naati/document/$doc")({
  loader: async ({ params }): Promise<{ d: NaatiDocLanding }> => {
    const { getNaatiDocLanding } = await import("@/content/naati-corpus/documents");
    const d = getNaatiDocLanding(params.doc);
    if (!d) throw notFound();
    return { d };
  },
  component: NaatiDocPageTH,
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "ไม่พบประเภทเอกสาร" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { d } = loaderData;
    const title = `${d.titleTh} 2026 — นักแปล NAATI Certified (CPN)`;
    const description = `${d.titleTh}สำหรับยื่นวีซ่าออสเตรเลีย/นิวซีแลนด์ — ${d.turnaroundTh}`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: `/naati/document/${params.doc}`,
        type: "article",
        alternates: [{ lang: "en", path: `/en/naati/document/${params.doc}` }],
        keywords: [d.titleTh, `${d.titleTh} ราคา`, `แปล ${d.officialTh}`, "แปลเอกสาร naati", "นักแปล naati รับรอง"],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: `${site}/` },
              { name: "แปลเอกสาร NAATI", url: `${site}/naati` },
              { name: d.titleTh, url: `${site}/naati/document/${params.doc}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(docFaqs(d))) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/naati/document/${params.doc}`)) },
      ],
    };
  },
});

function NaatiDocPageTH() {
  const { d } = Route.useLoaderData();
  const faqs = docFaqs(d);
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <a href="/naati" className="hover:underline">แปลเอกสาร NAATI</a> <span>/</span> <span>{d.titleTh}</span>
        </nav>

        <h1 className="mb-3 text-3xl font-bold md:text-4xl">{d.titleTh} — ยื่นวีซ่าออสเตรเลีย/นิวซีแลนด์ (2026)</h1>
        <p className="mb-6 text-lg text-muted-foreground">
          เอกสารอ้างอิง: <strong>{d.officialTh}</strong>
        </p>

        <div className="mb-8 flex flex-wrap gap-2 text-sm">
          <a href="/contact" className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground">ขอใบเสนอราคาฟรี →</a>
          <a href="/naati/faq" className="rounded-md border px-4 py-2 font-medium hover:bg-muted">คำถามที่พบบ่อย NAATI</a>
          <a href={`/en/naati/document/${d.slug}`} className="rounded-md border px-4 py-2 text-muted-foreground hover:bg-muted">English →</a>
        </div>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">ใช้ทำอะไร / ยื่นกับใคร</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{d.useCaseTh}</p>
        </section>

        <section className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="mb-3 text-xl font-bold">เตรียมเอกสารก่อนแปล</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {d.prepareTh.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
          <p className="mt-4 text-sm text-muted-foreground"><strong>ระยะเวลา:</strong> {d.turnaroundTh}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">ข้อผิดพลาดที่ทำให้เอกสารถูกตีกลับ</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {d.pitfallsTh.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">คำถามที่พบบ่อย — {d.titleTh}</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="rounded-lg border bg-card p-4" {...(i < 3 ? { open: true } : {})}>
                <summary className="cursor-pointer text-sm font-semibold">{f.q}</summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">แปล NAATI เอกสารประเภทอื่น</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NAATI_DOC_META.filter((x) => x.slug !== d.slug).map((x) => (
              <a key={x.slug} href={`/naati/document/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.titleTh}
              </a>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">บริการ NAATI แยกตามจังหวัด</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NAATI_PROVINCE_META.map((p) => (
              <a key={p.slug} href={`/naati/province/${p.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                NAATI {p.provinceTh}
              </a>
            ))}
          </div>
        </section>

        <PageGallery category="naati" seed="naati-document" locale="th" />
        <FaqSourceNote topic="translation" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}
